import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../store/revisione53.reducers';
import { ToastrService } from 'ngx-toastr';
import { Observable, map, take } from 'rxjs';
import { Revisione53CoreSelectors } from '../store/selectors/core.selectors';
import { Revisione53CoreActions } from '../store/revisione53.actions';
import { filterMatricolaVerbale } from '@shared/operators/filter-matricola-verbale.operator';
import { MyVerbale } from '../models/interfaces/revisione.interface';
import { inject } from '@angular/core';
import { cloneDeep, isEqual } from 'lodash-es';
import { controlFormStatus } from '@shared/helpers/utils.helper';
import { DatiIdentificativiFormService } from '../forms/dati-identificativi/dati-identificativi-form.service';
import { SectionFormServiceRef } from '../form-service-orchestrator/form-orchestrator.types';
import { FormSettlingService } from '../form-service-orchestrator/form-settling.service';

export abstract class BaseRevisioneFormService<TWarningService = unknown, TValidators = unknown> implements SectionFormServiceRef {
    isValid$: Observable<boolean>;

    get dependencyPaths(): Set<string> {
        return new Set();
    }

    protected fb = inject(FormBuilder);
    protected store = inject<Store<Revisione53State>>(Store);
    protected toastrService = inject(ToastrService);
    protected settlingService = inject(FormSettlingService);
    protected _form: FormGroup;
    protected _warnings: TWarningService;

    constructor(protected warningService?: TWarningService, protected validatorsService?: TValidators, protected datiIdentificativiFormService?: DatiIdentificativiFormService) {
        this._warnings = this.warningService;
        this._form = this.initForm();
        // Gate only on bulk settling (initial load / XML import), not on every store emission,
        // so single-field edits aren't frozen behind STORE_QUIET_MS.
        this.isValid$ = controlFormStatus(
            this._form,
            this.settlingService.settling$.pipe(map((settling) => !settling))
        );
        this.afterFormInitialize();
        this.populateForm();
    }

    get form(): FormGroup {
        if (!this._form) {
            this.toastrService.error('Il form non è stato inizializzato');
            throw new Error('Il form non è stato inizializzato');
        }

        this.populateForm();
        return this._form;
    }

    get warnings(): TWarningService | undefined {
        return this._warnings;
    }

    refreshFormData(emitEvent = true): void {
        this.populateForm(emitEvent);
    }

    /**
     * Synchronously pushes the current form value to the store, bypassing the debounced writers
     * (FormStoreHelpers / listeners). Used before reading the store (e.g. XML export) so a just-edited
     * field is never lost. Reads `_form` directly on purpose: `get form()` would trigger populateForm()
     * and re-patch from the (possibly stale) store, reverting the pending edit.
     */
    flushToStore(): void {
        if (!this._form) return;
        this.store.dispatch(Revisione53CoreActions.updateMyVerbale({ my_Verbale: cloneDeep(this._form.getRawValue()) }));
    }

    initListener(): void {}

    protected abstract initForm(): FormGroup;

    protected beforePopulateForm(myVerbale: MyVerbale): void {}

    protected afterFormInitialize(): void {}

    protected afterPopulateForm(myVerbale: MyVerbale): void {}

    protected populateForm(emitEvent = false): void {
        this.store
            .select(Revisione53CoreSelectors.getMyVerbale)
            .pipe(filterMatricolaVerbale(), take(1))
            .subscribe((myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                this.beforePopulateForm(myVerbaleClone);
                if (emitEvent) {
                    const valueBefore = this._form.getRawValue();
                    this._form.patchValue(myVerbaleClone, { emitEvent: false });
                    const hasChanged = !isEqual(valueBefore, this._form.getRawValue());
                    if (hasChanged) {
                        this._form.patchValue(myVerbaleClone, { emitEvent: true });
                    }
                    // emit updateValueAndValidity only when data changed, otherwise FormStoreHelpers
                    // would dispatch an identical store update on every orchestrator refresh → infinite loop
                    this._form.updateValueAndValidity({ emitEvent: hasChanged });
                } else {
                    this._form.patchValue(myVerbaleClone, { emitEvent: false });
                    this._form.updateValueAndValidity();
                }
                this.afterPopulateForm(myVerbaleClone);
            });
    }
}
